import {
	ArcElement,
	BarElement,
	CategoryScale,
	ChartData,
	Chart as ChartJS,
	ChartOptions,
	Legend,
	LineElement,
	LinearScale,
	PointElement,
	Tooltip,
} from 'chart.js';
import MarkdownIt from 'markdown-it';
import moment from 'moment';
import Mustache from 'mustache';
import { rgba } from 'polished';
import { memo, useEffect, useMemo, useState } from 'react';
import { Bar, Line, Pie } from 'react-chartjs-2';
import { useTranslation } from 'react-i18next';
import { Box } from 'reflexbox';
import styled from 'styled-components';

import {
	type AnalyticsChartSpec,
	type AnalyticsGridSpec,
	type AnalyticsSpec,
	extractLocalizedText,
	parseAnalyticsSpec,
	useAnalytics,
} from '@treams/treams-analytics';
import { DataGrid, Paragraph, Skeleton, tokens } from '@treams/treams-ui';

ChartJS.register(
	ArcElement,
	BarElement,
	CategoryScale,
	Legend,
	LineElement,
	LinearScale,
	PointElement,
	Tooltip,
);

type Row = Record<string, unknown>;

const BlockCard = styled(Box)`
	background: transparent;
	margin-top: ${tokens.gap.element}px;
`;

const ErrorCard = styled(BlockCard)`
	border-color: ${tokens.error.border};
`;

// AG Grid's root always spans its container; with content-sized columns the
// grid should instead shrink-wrap to its columns and treat the full component
// width as a maximum.
const GridFitWrapper = styled.div`
	.ag-root-wrapper {
		max-width: 100%;

		/* Close the Excel-style frame: rows/cells only draw bottom and right
		   gridlines, so the top and outer left/right come from the wrapper,
		   reusing the theme's own border definitions. The bottom line is the
		   last row's own border. */
		border-top: var(--ag-row-border);
		border-bottom: var(--ag-row-border);
		border-left: var(--ag-column-border);
		border-right: var(--ag-column-border);
		margin-bottom: 5px;
	}

	/* The header only draws a partial-height separator after the last column;
	   make it full-height so the rightmost gridline is continuous from the
	   header through the rows. */
	.ag-header-cell.ag-column-last::after {
		height: 100%;
		top: 0;
	}

	/* The wrapper's bottom frame line replaces the last row's own border;
	   keeping both would double the line. */
	.ag-row-last {
		border-bottom: none;
	}

	/* Cell text is selectable (enableCellTextSelection), so advertise it. */
	.ag-cell {
		cursor: text;
	}

	/* AG Grid reserves 150px of body height in auto-height layout so empty
	   grids have room for the "no rows" overlay; analytics grids always have
	   rows, so let short results collapse to their natural height. */
	.ag-layout-auto-height {
		.ag-center-cols-container,
		.ag-center-cols-viewport {
			min-height: 0;
		}
	}
`;

// Charts wider than the bubble (vertical bars with many categories) scroll
// horizontally instead of compressing their category labels away.
const ChartScrollBox = styled(Box)`
	overflow-x: auto;
	overflow-y: hidden;
`;

function unwrapLocalized(value: unknown, language: string): string {
	if (value === null || value === undefined) return '';
	if (typeof value === 'string') return value;

	if (Array.isArray(value)) return extractLocalizedText(value, language);

	if (typeof value === 'object') {
		const record = value as Record<string, unknown>;
		const preferred = record[language] ?? record.en ?? Object.values(record)[0];

		return preferred === undefined || preferred === null ? '' : String(preferred);
	}

	return String(value);
}

function formatCell(value: unknown, language: string): string {
	if (value === null || value === undefined) return '';
	if (value instanceof Date) return moment(value).format('ll');
	if (typeof value === 'object') return unwrapLocalized(value, language);

	return String(value);
}

/**
 * Recursively formats a row for template rendering: dates and localized
 * values become display strings, while nested objects and plain arrays stay
 * traversable so Mustache dot paths and section loops keep working.
 * @param value The row (or nested value) to format.
 * @param language The active UI language for localized values.
 */
function formatTemplateValue(value: unknown, language: string): unknown {
	if (value === null || value === undefined) return '';
	if (value instanceof Date) return moment(value).format('ll');

	if (Array.isArray(value)) {
		// extractLocalizedText accepts entries keyed by `language` or `languageID`.
		const isLocalized =
			value.length > 0 &&
			value.every(
				(item) =>
					!!item &&
					typeof item === 'object' &&
					('languageID' in (item as Row) || 'language' in (item as Row)),
			);

		if (isLocalized) return extractLocalizedText(value, language);

		return value.map((item) => formatTemplateValue(item, language));
	}

	if (typeof value === 'object') {
		const result: Row = {};

		for (const [key, inner] of Object.entries(value as Row)) {
			result[key] = formatTemplateValue(inner, language);
		}

		return result;
	}

	return value;
}

export function AnalyticsBlockSkeleton() {
	return (
		<BlockCard>
			<Skeleton.Box
				tint="subtle"
				height={400}
				style={{ borderRadius: tokens.radius.sm, width: '100%' }}
			/>
		</BlockCard>
	);
}

// A single line at text height, so a pending sentence answer reads as text
// (and not as a loading chart/table block).
export function AnalyticsTextSkeleton() {
	return (
		<Skeleton.Box
			tint="subtle"
			height={12}
			width={220}
			my="4px"
			style={{ borderRadius: tokens.radius.xs }}
		/>
	);
}

function AnalyticsGrid(props: { grid: AnalyticsGridSpec; rows: Row[] }) {
	const { i18n } = useTranslation();

	// Blend the grid into the (transparent) analytics card and swap AG Grid's
	// default grey/black text and borders for the analytics ink color.
	const themeParams = useMemo(
		() => ({
			backgroundColor: 'transparent',
			headerBackgroundColor: 'transparent',
			rowHoverColor: rgba(tokens.brand.icon, 0.06),
			dataFontSize: 13,
			foregroundColor: tokens.text.primary,
			textColor: tokens.text.primary,
			headerTextColor: tokens.text.primary,
			borderColor: rgba(tokens.text.primary, 0.3),
			// Spreadsheet-style gridlines: vertical borders between cells (rows
			// already have horizontal borders by default), inheriting borderColor.
			columnBorder: true,
			headerColumnBorder: true,
			// Separates the rows from the paging panel; the last row's own
			// bottom border is suppressed in GridFitWrapper because the wrapper
			// frame draws the grid's bottom line.
			footerRowBorder: true,
		}),
		[],
	);

	const columnTypes = useMemo(
		() => ({
			date: {
				valueFormatter: ({ value }: { value: unknown }) =>
					value ? moment(value as Date).format('ll') : '',
			},
			localized: {
				valueGetter: ({ data, colDef }: { data?: Row; colDef: { field?: string } }) => {
					const value = colDef.field
						?.split('.')
						.reduce<unknown>((acc, key) => (acc as Row | undefined)?.[key], data);

					return unwrapLocalized(value, i18n.language);
				},
			},
		}),
		[i18n.language],
	);

	return (
		<GridFitWrapper>
			<DataGrid<Row>
				rowData={props.rows}
				columnDefs={props.grid.columnDefs}
				columnTypes={columnTypes}
				themeParams={themeParams}
				rowHeight={22}
				// Size columns to their content instead of stretching them across
				// the full width; `flex: undefined` cancels the shared `flex: 1`
				// default, which would otherwise take precedence over auto-sizing.
				autoSizeStrategy={{ type: 'fitCellContents' }}
				// Cells suppress text selection by default; ensureDomOrder keeps
				// the DOM in row order so a selection spans rows sensibly.
				enableCellTextSelection
				ensureDomOrder
				defaultColDef={{ filter: true, resizable: true, flex: undefined }}
				pagination={props.rows.length > 25}
				paginationPageSize={25}
				paginationPageSizeSelector={false}
			/>
		</GridFitWrapper>
	);
}

function AnalyticsChart(props: { chart: AnalyticsChartSpec; rows: Row[] }) {
	const { i18n } = useTranslation();

	const { chart, rows } = props;

	// The accent pink at decreasing alpha, ordered opaque to translucent so
	// adjacent datasets and pie slices stay distinguishable.
	const palette = useMemo(
		() => [
			tokens.brand.icon,
			tokens.info.icon,
			rgba(tokens.brand.icon, 0.7),
			rgba(tokens.info.icon, 0.7),
			rgba(tokens.brand.icon, 0.5),
			rgba(tokens.info.icon, 0.5),
			rgba(tokens.brand.icon, 0.3),
			rgba(tokens.info.icon, 0.3),
		],
		[],
	);

	const labels = useMemo(
		() => rows.map((row) => formatCell(row[chart.labelsField], i18n.language)),
		[rows, chart.labelsField, i18n.language],
	);

	const data = useMemo<ChartData<'bar' | 'line' | 'pie'>>(() => {
		return {
			labels,
			datasets: chart.datasets.map((dataset, index) => {
				const values = rows.map((row) => {
					const value = row[dataset.field];

					return typeof value === 'number' ? value : Number(value) || 0;
				});

				const color = palette[index % palette.length];

				return {
					label: dataset.label,
					data: values,
					backgroundColor:
						chart.kind === 'pie' ? values.map((_, i) => palette[i % palette.length]) : color,
					borderColor: chart.kind === 'pie' ? 'transparent' : color,
				};
			}),
		};
	}, [labels, chart, rows, palette]);

	const options = useMemo<ChartOptions<'bar' | 'line' | 'pie'>>(() => {
		const showLegend = chart.kind === 'pie' || chart.datasets.length > 1;

		// Replace ChartJS' default grey/black with the analytics ink color for
		// all text, strokes and borders.
		const textColor = tokens.text.primary;
		const lineColor = rgba(tokens.text.primary, 0.2);

		const base: ChartOptions<'bar' | 'line' | 'pie'> = {
			responsive: true,
			maintainAspectRatio: false,
			plugins: { legend: { display: showLegend, labels: { color: textColor } } },
		};

		if (chart.kind === 'pie') return base;

		const categoryAxis = {
			stacked: chart.stacked ?? false,
			ticks: {
				color: textColor,
				font: { size: 11 },
				// Bars reserve space per category (see the sizing below), so
				// every label is rendered; lines keep autoSkip because dense
				// time series stay readable with intermediate labels skipped.
				...(chart.kind === 'bar' ? { autoSkip: false, maxRotation: 60 } : {}),
			},
			grid: { color: lineColor },
			border: { color: lineColor },
		};

		const valueAxis = {
			stacked: chart.stacked ?? false,
			beginAtZero: true,
			ticks: { color: textColor, font: { size: 11 } },
			grid: { color: lineColor },
			border: { color: lineColor },
		};

		return {
			...base,
			indexAxis: chart.horizontal ? ('y' as const) : ('x' as const),
			// Let bars fill most of their category slot (defaults leave 28%
			// of it as whitespace, which reads as overly large gaps).
			datasets: { bar: { categoryPercentage: 0.9, barPercentage: 0.95 } },
			scales: chart.horizontal
				? { x: valueAxis, y: categoryAxis }
				: { x: categoryAxis, y: valueAxis },
		};
	}, [chart]);

	// One guaranteed slot per category so every label can render: horizontal
	// bars grow in height, vertical bars in min-width (scrolling horizontally
	// via ChartScrollBox when wider than the bubble). Lines and pies keep a
	// fixed size.
	const isHorizontalBar = chart.kind === 'bar' && chart.horizontal;
	const isVerticalBar = chart.kind === 'bar' && !chart.horizontal;

	const height = isHorizontalBar ? Math.min(Math.max(labels.length * 18 + 90, 160), 2000) : 400;

	return (
		<ChartScrollBox mb={2}>
			<Box height={height} minWidth={isVerticalBar ? labels.length * 36 : undefined}>
				{chart.kind === 'bar' && (
					<Bar data={data as ChartData<'bar'>} options={options as ChartOptions<'bar'>} />
				)}
				{chart.kind === 'line' && (
					<Line data={data as ChartData<'line'>} options={options as ChartOptions<'line'>} />
				)}
				{chart.kind === 'pie' && (
					<Pie data={data as ChartData<'pie'>} options={options as ChartOptions<'pie'>} />
				)}
			</Box>
		</ChartScrollBox>
	);
}

// Inline markdown only (bold, italic, links); block-level constructs like
// lists or headings are not available inside a text answer.
const inlineMarkdown = new MarkdownIt({ breaks: true, linkify: true });

function AnalyticsText(props: { template: string; rows: Row[] }) {
	const { t, i18n } = useTranslation();

	const html = useMemo(() => {
		const rows = props.rows.map((row) => formatTemplateValue(row, i18n.language) as Row);
		const view = { ...(rows[0] ?? {}), rows, count: rows.length };

		try {
			// Escaping is left to markdown-it (HTML is escaped there); Mustache's
			// own HTML escaping would double-escape entities.
			const rendered = Mustache.render(props.template, view, {}, { escape: String });

			return inlineMarkdown.renderInline(rendered);
		} catch {
			return null;
		}
	}, [props.template, props.rows, i18n.language]);

	if (html === null) {
		return (
			<ErrorCard>
				<Paragraph>{t('aiAgent.analytics.invalidSpec')}</Paragraph>
			</ErrorCard>
		);
	}

	return <span dangerouslySetInnerHTML={{ __html: html }} />;
}

const AnalyticsResult = memo(function AnalyticsResult(props: { spec: AnalyticsSpec; rows: Row[] }) {
	const { t } = useTranslation();

	const textOnly = !!props.spec.text && !props.spec.chart && !props.spec.grid;

	if (props.rows.length === 0 && !textOnly) {
		return (
			<BlockCard>
				<Paragraph>{t('aiAgent.analytics.noData')}</Paragraph>
			</BlockCard>
		);
	}

	// A text-only answer renders as bare prose, without the block chrome.
	if (textOnly) return <AnalyticsText template={props.spec.text!} rows={props.rows} />;

	return (
		<BlockCard>
			{props.spec.text && (
				<Box mb={2}>
					<AnalyticsText template={props.spec.text} rows={props.rows} />
				</Box>
			)}
			{props.spec.chart && <AnalyticsChart chart={props.spec.chart} rows={props.rows} />}
			{props.spec.chart && props.spec.grid && <Box mt={1} />}
			{props.spec.grid && <AnalyticsGrid grid={props.spec.grid} rows={props.rows} />}
		</BlockCard>
	);
});

/**
 * Renders one fenced spec block of a `# Treams Analytics` response: parses and
 * validates the spec, executes its pipeline in the analytics worker, and
 * renders the resulting rows as a grid and/or chart.
 * @param props The component props.
 * @param props.specText The raw JSON text of the spec block.
 */
export const AnalyticsBlock = memo(function AnalyticsBlock(props: { specText: string }) {
	const { t } = useTranslation();
	const { requestQuery } = useAnalytics();

	const parsed = useMemo(() => parseAnalyticsSpec(props.specText), [props.specText]);

	const [rows, setRows] = useState<Row[] | null>(null);
	const [queryError, setQueryError] = useState<string | null>(null);

	useEffect(() => {
		if (!parsed.ok) return;

		let cancelled = false;

		setRows(null);
		setQueryError(null);

		// `parsed` is memoized on the specText string, so re-renders with an
		// unchanged spec keep the same reference and never re-execute the
		// pipeline; only an actual text change (or a remount) queries again.
		requestQuery(parsed.spec.source, parsed.spec.pipeline)
			.then((result) => {
				if (!cancelled) setRows(result);
			})
			.catch((error: Error) => {
				if (!cancelled) setQueryError(error.message);
			});

		return () => {
			cancelled = true;
		};
	}, [parsed, requestQuery]);

	if (!parsed.ok) {
		return (
			<ErrorCard>
				<Paragraph>{t('aiAgent.analytics.invalidSpec')}</Paragraph>
				<Paragraph fontSize={1}>{parsed.error}</Paragraph>
			</ErrorCard>
		);
	}

	if (queryError) {
		return (
			<ErrorCard>
				<Paragraph>{t('aiAgent.analytics.queryFailed')}</Paragraph>
				<Paragraph fontSize={1}>{queryError}</Paragraph>
			</ErrorCard>
		);
	}

	if (rows === null) {
		const textOnly = !!parsed.spec.text && !parsed.spec.chart && !parsed.spec.grid;

		return textOnly ? <AnalyticsTextSkeleton /> : <AnalyticsBlockSkeleton />;
	}

	return <AnalyticsResult spec={parsed.spec} rows={rows} />;
});
